import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "../api";

/** Days of the week the cron picks the digest up on. ISO codes. */
export type DigestDayCode = "SUN" | "MON" | "TUE" | "WED" | "THU" | "FRI" | "SAT";

/** Funnel sub-toggle map (one boolean per recognized event). */
export interface FunnelFieldToggles {
  impressions?: boolean;
  clicks?: boolean;
  addToCart?: boolean;
  beginCheckout?: boolean;
  purchase?: boolean;
  sessions?: boolean;
  newVisitors?: boolean;
}

/** Campaign-summary sub-toggle map (one boolean per column). */
export interface CampaignFieldToggles {
  impressions?: boolean;
  clicks?: boolean;
  ctr?: boolean;
  addToCart?: boolean;
  beginCheckout?: boolean;
  bookingsCount?: boolean;
  bookingsRevenue?: boolean;
  cvr?: boolean;
}

/** Section that supports sub-toggles. `enabled` gates the whole section;
 *  `fields` is the per-row / per-column map. */
export interface SubToggleSection<F> {
  enabled?: boolean;
  fields?: F;
}

export interface DigestContentSections {
  webTraffic?: boolean;
  geographyAndDevice?: boolean;
  bookings?: boolean;
  marketContext?: boolean;
  additionalInsights?: boolean;
  funnel?: SubToggleSection<FunnelFieldToggles>;
  campaigns?: SubToggleSection<CampaignFieldToggles>;
  abandonedCart?: boolean;
  feederMarkets?: boolean;
}

/** Shape returned by GET /marketing/:userId/digests/available-fields.
 *  Drives sub-toggle visibility in the form. */
export interface DigestAvailableFields {
  funnel: {
    impressions: boolean;
    clicks: boolean;
    addToCart: boolean;
    beginCheckout: boolean;
    purchase: boolean;
    sessions: boolean;
    newVisitors: boolean;
  };
  campaigns: {
    impressions: boolean;
    clicks: boolean;
    ctr: boolean;
    addToCart: boolean;
    beginCheckout: boolean;
    bookingsCount: boolean;
    bookingsRevenue: boolean;
    cvr: boolean;
  };
  abandonedCart: boolean;
  feederMarkets: boolean;
  isGa4Only: boolean;
}

/** Server shape for a single digest config. */
export interface MarketingDigest {
  id: string;
  userId: string;
  name: string;
  paused: boolean;
  repeatEveryWeeks: number;
  repeatOnDays: DigestDayCode[];
  recipients: string[];
  contentSections: DigestContentSections;
  timezone: string;
  lastSentAt: string | null;
  lastSendStatus: "success" | "failed" | null;
  lastSendError: string | null;
  lastScheduledFor: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateDigestPayload {
  name: string;
  repeatEveryWeeks: number;
  repeatOnDays: DigestDayCode[];
  recipients: string[];
  contentSections?: DigestContentSections;
}

export interface UpdateDigestPayload {
  name?: string;
  repeatEveryWeeks?: number;
  repeatOnDays?: DigestDayCode[];
  recipients?: string[];
  contentSections?: DigestContentSections;
  paused?: boolean;
}

const digestsKey = (userId: string | undefined) =>
  ["marketing", "digests", userId] as const;

/** Probe which funnel rows / campaign columns / sections the customer
 *  has data for. Hides sub-toggles the user would never see results
 *  from. 5-minute staleTime because the underlying data only refreshes
 *  on the daily cron tick. */
export function useDigestAvailableFields(userId: string | undefined) {
  return useQuery<DigestAvailableFields>({
    queryKey: ["marketing", "digests", "available-fields", userId] as const,
    queryFn: () =>
      api.marketing.digests.availableFields(userId as string) as Promise<
        DigestAvailableFields
      >,
    enabled: !!userId,
    staleTime: 5 * 60 * 1000,
  });
}

/** List all email digests configured by the user. */
export function useMarketingDigests(userId: string | undefined) {
  return useQuery<MarketingDigest[]>({
    queryKey: digestsKey(userId),
    queryFn: () =>
      api.marketing.digests.list(userId as string) as Promise<
        MarketingDigest[]
      >,
    enabled: !!userId,
    staleTime: 60 * 1000,
  });
}

/** Create a new digest. Invalidates the list on success. */
export function useCreateMarketingDigest(userId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateDigestPayload) =>
      api.marketing.digests.create(
        userId as string,
        payload as unknown as Record<string, unknown>,
      ) as Promise<MarketingDigest>,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: digestsKey(userId) });
    },
  });
}

/** Update an existing digest. Patches the cached list optimistically? Not
 *  yet — keep it simple: invalidate on success and let the list refetch. */
export function useUpdateMarketingDigest(
  userId: string | undefined,
  digestId: string | undefined,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: UpdateDigestPayload) =>
      api.marketing.digests.update(
        userId as string,
        digestId as string,
        payload as unknown as Record<string, unknown>,
      ) as Promise<MarketingDigest>,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: digestsKey(userId) });
    },
  });
}

/** Pause a digest (cron will skip it). */
export function usePauseMarketingDigest(userId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (digestId: string) =>
      api.marketing.digests.pause(userId as string, digestId) as Promise<
        MarketingDigest
      >,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: digestsKey(userId) });
    },
  });
}

/** Resume a paused digest. */
export function useResumeMarketingDigest(userId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (digestId: string) =>
      api.marketing.digests.resume(userId as string, digestId) as Promise<
        MarketingDigest
      >,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: digestsKey(userId) });
    },
  });
}

/** Soft-delete a digest. */
export function useDeleteMarketingDigest(userId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (digestId: string) =>
      api.marketing.digests.delete(userId as string, digestId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: digestsKey(userId) });
    },
  });
}

export interface DigestPreviewResponse {
  subject: string;
  preheader: string;
  html: string;
  text: string;
}

/** Render the digest without sending. Used by the Preview button to
 *  show the rendered HTML in a modal so the customer can inspect copy. */
export function usePreviewMarketingDigest(userId: string | undefined) {
  return useMutation({
    mutationFn: (digestId: string) =>
      api.marketing.digests.preview(
        userId as string,
        digestId,
      ) as Promise<DigestPreviewResponse>,
  });
}

export interface DigestSendNowResponse {
  digestId: string;
  recipients: string[];
  subject: string;
  status: "sent" | "skipped" | "failed";
  detail?: string;
}

/** Manually trigger a send. Optional `recipients` override sends to a
 *  specific test address instead of the digest's configured list - used
 *  by the "Send test to me" button in the form. */
export function useSendNowMarketingDigest(userId: string | undefined) {
  return useMutation({
    mutationFn: (args: { digestId: string; recipients?: string[] }) =>
      api.marketing.digests.sendNow(
        userId as string,
        args.digestId,
        args.recipients ? { recipients: args.recipients } : undefined,
      ) as Promise<DigestSendNowResponse>,
  });
}
